// frontend/src/components/TaskCreationBar.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../api';
import MentalLoadScreen from './MentalLoadScreen';
import './TaskRecipeView.css';
import { useNotification } from '../context/NotificationContext';
import { useI18n } from '../i18n';
import { useAgentCommands } from '../contexts/AgentCommandContext';

export default function TaskCreationBar({
  onGenerated,
  onRateLimited,
  prompt: externalPrompt,
  setPrompt: setExternalPrompt,
  rightActions
}) {
  const { addNotification } = useNotification();
  const { t } = useI18n();
  const { registerCommand, unregisterCommand } = useAgentCommands();
  const [internalPrompt, setInternalPrompt] = useState('');
  const textareaRef = useRef(null);

  const prompt = externalPrompt !== undefined ? externalPrompt : internalPrompt;
  const setPrompt = setExternalPrompt || setInternalPrompt;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [prompt]);

  const submitPrompt = useCallback(async (promptOverride) => {
    const requestedPrompt = String(promptOverride ?? prompt).trim();
    setLoading(true);

    try {
        const res = await api.get('/tareas/generate', {
            params: { prompt: requestedPrompt || undefined }
        });

        if (res.data && res.data.ok && res.data.task?.id) {
            onGenerated(res.data);
            setPrompt('');
            if (textareaRef.current) textareaRef.current.style.height = 'auto';
            return { ok: true, taskId: res.data.task.id };
        }
        addNotification(t('tasks.createError'), "error");
        return { ok: false, reason: res.data?.ok ? 'missing_task_id' : 'create_failed' };
    } catch (err) {
        console.error("Error generando:", err);
        if (err.response && err.response.status === 429) {
            const resetHeader = err.response.headers['ratelimit-reset'] || err.response.headers['retry-after'];
            if (resetHeader) {
                let val = parseInt(resetHeader, 10);
                let finalResetTime = 0;
                if (val < 31536000) {
                     finalResetTime = Date.now() + (val * 1000);
                } else {
                     if (val < 100000000000) {
                         finalResetTime = val * 1000;
                     } else {
                         finalResetTime = val;
                     }
                }
                if (onRateLimited) onRateLimited(finalResetTime);
            } else {
                 if (onRateLimited) onRateLimited(Date.now() + 10 * 60 * 1000);
            }
        } else {
            addNotification(t('common.connectionError'), "error");
        }
        return { ok: false, reason: err.response?.status === 429 ? 'rate_limited' : 'request_failed' };
    } finally {
        setLoading(false);
    }
  }, [addNotification, onGenerated, onRateLimited, prompt, setPrompt, t]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    await submitPrompt();
  };

  const handleKeyDown = (e) => {
      if (e.key === 'Enter' && e.altKey) {
          e.preventDefault();
          handleSubmit();
      }
  };

  useEffect(() => {
    registerCommand('tasks_prepare_task', (text) => {
      const cleanText = String(text || '').trim();
      if (!cleanText) return false;
      setPrompt(cleanText);
      window.requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
      return true;
    });

    registerCommand('tasks_create_task', async (text) => {
      const cleanText = String(text || '').trim();
      if (!cleanText) return { ok: false, reason: 'empty_text' };
      return submitPrompt(cleanText);
    });

    return () => {
      unregisterCommand('tasks_prepare_task');
      unregisterCommand('tasks_create_task');
    };
  }, [registerCommand, setPrompt, submitPrompt, unregisterCommand]);

  return (
    <div className="task-creation-bar bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-700">
        {loading && <MentalLoadScreen loading={loading} message={t('tasks.creatingTask')} />}
        <form onSubmit={handleSubmit} className="task-creation-form">
            <textarea
                id="task-main-input"
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('tasks.taskPrompt')}
                className="task-input bg-gray-50 text-gray-900 border-gray-300 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                aria-label={t('tasks.taskDescription')}
                rows={1}
            />
            <button id="btn-task-confirm" type="submit" disabled={loading} className={`task-submit-btn bg-emerald-600 text-white dark:bg-emerald-700 ${loading ? 'btn-processing' : ''}`}>
                {loading ? t('common.generating') : (prompt.trim() ? t('common.create') : t('tasks.surpriseMe'))}
            </button>
            <div className="task-audio-actions">{rightActions}</div>
            <div className="input-hint">{t('tasks.inputHint')}</div>
        </form>
    </div>
  );
}
